import { motion } from 'framer-motion';
import { Users, Sparkles, TrendingUp, ArrowRight } from 'lucide-react';

const features = [
  {
    icon: Users,
    title: 'The Talent Operating System',
    description:
      'Build a team of up to 8 people or fill one critical skillset with pre-vetted and managed talent, matched and onboarded in less than 48 hours with a dedicated Talent Manager.',
    bullets: [
      'Pre-vetted specialists matched to your exact needs',
      'Dedicated Talent Manager from onboarding through delivery',
      'Deploy in under 48 hours',
    ],
    ctaLabel: 'Start Building Your Team',
    ctaHref: 'https://vibe.hirex8.com/register?type=client',
  },
  {
    icon: Sparkles,
    title: 'Hire a POD',
    description:
      'Project Manager + Resources starting at $3,500.00 per month. Your projects managed and delivered for you, designed specifically for Digital Agency owners. Best for engineering-heavy needs.',
    bullets: [
      'Purpose-built pod structure for agency execution',
      'Managed delivery with clear ownership',
      'Built to scale engineering-heavy project pipelines',
    ],
    ctaLabel: 'Hire a POD',
    ctaHref: '/services/pod',
  },
  {
    icon: TrendingUp,
    title: 'Consultation',
    description:
      'Schedule a pre-vetted senior operations specialist for building proposals or drafting specifications in Website Development, Mobile App Development, AI Automation, and AI Chat Bot Development. Starting at $100 per consultation.',
    bullets: [
      'Proposal and scope support for technical client work',
      'Specification drafting from senior operators',
      'Fast support for high-stakes sales conversations',
    ],
    ctaLabel: 'Book a Consultation',
    ctaHref: '/services/consultation',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } },
};

export const AgencyEngineSection = () => (
  <section id="services" className="py-24 md:py-32 border-t border-border bg-background overflow-hidden">
    <div className="section-container">
      {/* Header */}
      <div className="mb-14 md:mb-20">
        <motion.p
          className="section-label mb-5"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          Scale Without Hiring
        </motion.p>

        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-end">
          <motion.h2
            className="text-[44px] sm:text-[52px] lg:text-[60px] font-extrabold tracking-tighter text-foreground leading-none max-w-2xl"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.65, delay: 0.1 }}
          >
            Add Capacity. Launch New Services. Deliver Faster.
          </motion.h2>

          <motion.p
            className="text-[17px] text-muted-foreground leading-[1.55] max-w-sm"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Get instant access to white-labeled Human + AI talent across design, content, SEO, engineering, and automation—deployed in 48 hours, without hiring or increasing overhead.
          </motion.p>
        </div>
      </div>

      {/* Swiss divider */}
      <div className="swiss-divider mb-14" />

      {/* Feature cards */}
      <motion.div
        className="grid md:grid-cols-3 gap-6"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        {features.map((f) => (
          <motion.div key={f.title} variants={itemVariants} className="feature-card flex flex-col">
            <div className="w-10 h-10 rounded-xl bg-apple-blue-light flex items-center justify-center mb-5">
              <f.icon size={18} className="text-apple-blue" />
            </div>
            <h3 className="text-[20px] font-bold text-foreground tracking-tight mb-3 leading-snug">{f.title}</h3>
            <p className="text-[15px] text-muted-foreground leading-[1.6] mb-5">{f.description}</p>
            <ul className="space-y-2 mt-auto">
              {f.bullets.map(b => (
                <li key={b} className="flex items-start gap-2 text-[14px] text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-apple-blue flex-shrink-0 mt-[7px]" />
                  {b}
                </li>
              ))}
            </ul>
            <a
              href={f.ctaHref}
              className="mt-6 inline-flex items-center gap-2 text-[15px] font-semibold text-apple-blue hover:opacity-85 transition-opacity"
            >
              {f.ctaLabel} <ArrowRight size={15} />
            </a>
          </motion.div>
        ))}
      </motion.div>

    </div>
  </section>
);
